import { useState } from "react";
import { Menu, X, Home, FileText, Mail, Users, FileCheck, FileSpreadsheet, FileSignature, FileX, Target, ShieldCheck, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMobileServices, setShowMobileServices] = useState(false);

  const ServicesDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-gray-800 hover:text-gray-600 transition-colors flex items-center gap-2 focus:outline-none">
        <FileText size={20} className="opacity-75" />
        Serviços
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 mt-1 p-1 bg-white rounded-lg shadow-lg border border-gray-100 animate-fade-in">
        <DropdownMenuItem asChild>
          <Link to="/servicos/protestos" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
            <FileCheck size={16} className="opacity-75" />
            Protestos
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/servicos/certidoes" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
            <FileSpreadsheet size={16} className="opacity-75" />
            Certidões
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/servicos/anuencias" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
            <FileSignature size={16} className="opacity-75" />
            Anuências
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/servicos/cancelamentos" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
            <FileX size={16} className="opacity-75" />
            Cancelamentos
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const MobileServicesMenu = () => (
    <div className="pl-8 space-y-2 animate-fade-in">
      <Link to="/servicos/protestos" className="flex items-center gap-2 text-gray-800" onClick={() => setIsOpen(false)}>
        <FileCheck size={16} className="opacity-75" />
        Protestos
      </Link>
      <Link to="/servicos/certidoes" className="flex items-center gap-2 text-gray-800" onClick={() => setIsOpen(false)}>
        <FileSpreadsheet size={16} className="opacity-75" />
        Certidões
      </Link>
      <Link to="/servicos/anuencias" className="flex items-center gap-2 text-gray-800" onClick={() => setIsOpen(false)}>
        <FileSignature size={16} className="opacity-75" />
        Anuências
      </Link>
      <Link to="/servicos/cancelamentos" className="flex items-center gap-2 text-gray-800" onClick={() => setIsOpen(false)}>
        <FileX size={16} className="opacity-75" />
        Cancelamentos
      </Link>
    </div>
  );

  return (
    <nav className="bg-white shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center">
            <img
              src="/lovable-uploads/f2b268c1-da0b-4f45-9377-9390eb61392e.png"
              alt="3º Tabelionato de Protesto de Curitiba"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex gap-4 text-sm">
            <NavLink to="/" icon={Home}>Início</NavLink>
            <NavLink to="/missao" icon={Target}>Missão</NavLink>
            <ServicesDropdown />
            <NavLink to="/corregedoria" icon={Users}>Corregedoria</NavLink>
            <NavLink to="/alerta" icon={ShieldCheck}>Alerta</NavLink>
            <NavLink to="/links-e-apps" icon={LinkIcon}>Links Úteis / Apps</NavLink>
            <NavLink to="/contato" icon={Mail}>Contato</NavLink>
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(!isOpen)}
            className="xl:hidden"
            aria-label={isOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="xl:hidden pb-4 animate-fade-in">
            <NavLink mobile to="/" icon={Home} onClick={() => setIsOpen(false)}>
              Início
            </NavLink>
            <NavLink mobile to="/missao" icon={Target} onClick={() => setIsOpen(false)}>
              Missão
            </NavLink>
            <div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 py-2 px-0 text-foreground"
                onClick={() => setShowMobileServices(!showMobileServices)}
                aria-expanded={showMobileServices}
              >
                <FileText size={20} className="opacity-75" />
                Serviços
              </Button>
              {showMobileServices && <MobileServicesMenu />}
            </div>
            <NavLink mobile to="/corregedoria" icon={Users} onClick={() => setIsOpen(false)}>
              Corregedoria
            </NavLink>
            <NavLink mobile to="/alerta" icon={ShieldCheck} onClick={() => setIsOpen(false)}>
              Alerta
            </NavLink>
            <NavLink mobile to="/links-e-apps" icon={LinkIcon} onClick={() => setIsOpen(false)}>
              Links Úteis / Apps
            </NavLink>
            <NavLink mobile to="/contato" icon={Mail} onClick={() => setIsOpen(false)}>
              Contato
            </NavLink>
          </div>
        )}
      </div>
    </nav>
  );
};

const NavLink = ({
  children,
  to,
  mobile,
  icon: Icon,
  onClick,
}: {
  children: React.ReactNode;
  to: string;
  mobile?: boolean;
  icon?: React.ComponentType<any>;
  onClick?: () => void;
}) => (
  <Link
    to={to}
    onClick={onClick}
    className={`text-foreground hover:text-muted-foreground transition-colors flex items-center gap-2 ${
      mobile ? "block py-2" : ""
    }`}
  >
    {Icon && <Icon size={20} className="opacity-75" />}
    {children}
  </Link>
);